// src/pages/EditCampaignPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    doc, getDoc, updateDoc, serverTimestamp, collection, query, getDocs, orderBy, where, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path if needed
import { useAuth } from '../context/AuthContext'; // Adjust path if needed
// --- Import the CSS Module ---
import styles from '../styles/FormPage.module.css'; // Use the professional form styles

// Define roles allowed to edit campaigns
const campaignEditRoles = ['Manager', 'Sales', 'Accounts Manager'];

// Service Types - Define consistently (consider moving to a shared config)
const serviceTypes = [
    { key: 'design', label: 'Design' },
    { key: 'reelContentCreation', label: 'Reel (Content Creation)' },
    { key: 'reelPro', label: 'Reel (Pro)' },
    { key: 'video', label: 'Video' },
    { key: 'motionGraphics', label: 'Motion Graphics' },
    { key: 'cgi', label: 'CGI' },
    { key: 'sponsorshipManagement', label: 'Sponsorship Management' },
    { key: 'prints', label: 'Prints' } // Special case handled below
];

// Helper to format Firestore Timestamp or Date string to YYYY-MM-DD
const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
        const date = (dateValue instanceof Timestamp) ? dateValue.toDate() : new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (error) {
        console.error("Error formatting date:", error);
        return '';
    }
};

const EditCampaignPage = () => {
    const { campaignId } = useParams(); // Get campaign ID from URL
    const navigate = useNavigate();
    const { currentUser, userRole, loading: authLoading } = useAuth();

    // --- Form State ---
    const [clientName, setClientName] = useState('');
    const [clientId, setClientId] = useState(''); // Store client ID associated with campaign
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [services, setServices] = useState({}); // Object to store service counts
    const [printsDetails, setPrintsDetails] = useState(''); // Specific state for prints details
    const [originalCampaignName, setOriginalCampaignName] = useState(''); // For display title

    // --- Helper State ---
    const [clients, setClients] = useState([]); // For client dropdown
    const [loading, setLoading] = useState(true); // Loading campaign data
    const [updating, setUpdating] = useState(false); // Form submission loading
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Fetch Clients for dropdown
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const clientsRef = collection(db, 'clients');
                const q = query(clientsRef, orderBy('name', 'asc'));
                const snapshot = await getDocs(q);
                const clientList = snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name }));
                setClients(clientList);
            } catch (err) {
                console.error("Error fetching clients:", err);
                setError("Failed to load client list.");
            }
        };
        fetchClients();
    }, []);

    // Fetch Campaign Data
    const fetchCampaignData = useCallback(async () => {
        // Wait for auth and client list
        if (authLoading || clients.length === 0) {
             setLoading(true); return;
        }
        // Authorization check
        if (!userRole || !campaignEditRoles.includes(userRole)) {
            setError("Unauthorized: You cannot edit campaigns.");
            setLoading(false); return;
        }

        setLoading(true); setError('');
        try {
            const campaignRef = doc(db, 'campaigns', campaignId);
            const docSnap = await getDoc(campaignRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setClientName(data.clientName || '');
                setClientId(data.clientId || '');
                setOriginalCampaignName(data.clientName || `Campaign ${campaignId.substring(0, 5)}`);
                setDescription(data.description || '');
                setStartDate(formatDateForInput(data.startDate));
                setEndDate(formatDateForInput(data.endDate));

                const initialServices = {};
                serviceTypes.forEach(st => {
                    if (st.key !== 'prints') {
                        initialServices[st.key] = data.services?.[st.key] || 0;
                    }
                });
                 initialServices['prints'] = data.services?.prints?.count || 0;
                 setPrintsDetails(data.services?.prints?.details || '');
                setServices(initialServices);

            } else {
                setError("Campaign not found.");
                setOriginalCampaignName('');
            }
        } catch (err) {
            console.error("Error fetching campaign:", err);
            setError("Failed to load campaign data.");
        } finally {
            setLoading(false);
        }
    }, [campaignId, userRole, authLoading, clients]); // Add clients as dependency

    useEffect(() => {
        fetchCampaignData();
    }, [fetchCampaignData]); // Fetch data when function reference changes

    // Handle service count changes
    const handleServiceChange = (key, value) => {
        const count = parseInt(value, 10);
        setServices(prev => ({
            ...prev,
            [key]: isNaN(count) || count < 0 ? 0 : count // Ensure non-negative integer
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
         e.preventDefault();
         setError(''); setSuccessMessage('');

         // Validation
         if (!clientId) { setError("Please select a client."); return; }
         if (!description.trim()) { setError("Description is required."); return; }
         if (!startDate || !endDate) { setError("Start and End dates are required."); return; }
         if (new Date(startDate) > new Date(endDate)) { setError("End date cannot be before start date."); return; }

         setUpdating(true);

         const selectedClient = clients.find(c => c.id === clientId);
         const currentClientName = selectedClient ? selectedClient.name : 'Unknown Client';

         // Prepare services object for Firestore
         const servicesToSave = {};
         Object.entries(services).forEach(([key, count]) => {
             if (key === 'prints') {
                 if (count > 0 || printsDetails.trim()) {
                      servicesToSave.prints = { count: count || 0, details: printsDetails.trim() };
                 }
             } else if (count > 0) {
                 servicesToSave[key] = count;
             }
         });

         try {
             const campaignRef = doc(db, 'campaigns', campaignId);
             await updateDoc(campaignRef, {
                 clientId: clientId,
                 clientName: currentClientName,
                 description: description.trim(),
                 startDate: Timestamp.fromDate(new Date(startDate + 'T00:00:00')),
                 endDate: Timestamp.fromDate(new Date(endDate + 'T00:00:00')),
                 services: servicesToSave,
                 updatedAt: serverTimestamp(),
             });

             setSuccessMessage("Campaign updated successfully!");
             // Update original name in case client changed
             setOriginalCampaignName(currentClientName);
             setTimeout(() => navigate(`/campaigns/${campaignId}/plan`), 1500); // Navigate back

         } catch (err) {
             console.error("Error updating campaign:", err);
             setError("Failed to update campaign. Please try again.");
         } finally {
             setUpdating(false);
         }
     };

    // --- Render Logic ---
    if (authLoading || loading) { return <div className={styles.loadingIndicator}>Loading...</div>; }
    if (error && !originalCampaignName) { // Show error if loading failed and we don't have campaign data
         return ( <div className={`${styles.pageContainer} ${styles.errorMessageContainer}`}> <p className={styles.errorMessage}>Error: {error}</p> <button onClick={() => navigate('/campaigns')} className={`${styles.formButton} ${styles.cancelButton}`} style={{marginTop: '1rem'}}>Back to Campaigns</button> </div> );
     }
     // This check prevents rendering form if user role is loaded but not authorized
     if (!userRole || !campaignEditRoles.includes(userRole)) {
          return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>Access Denied.</div>;
     }


    return (
        // Apply styles from FormPage.module.css
        <div className={styles.formContainer}>
            <h2 className={styles.title}>Edit Campaign: {originalCampaignName}</h2>

            {error && <p className={styles.errorMessage}>{error}</p>}
            {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

            <form onSubmit={handleSubmit} className={styles.form}>
                {/* Client Selection */}
                <div className={styles.inputGroup}>
                    <label htmlFor="client">Client</label>
                    <select
                        id="client"
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        required
                        disabled={updating || clients.length === 0}
                        className={styles.selectInput} // Added class for select styling if needed
                    >
                        <option value="" disabled>-- Select Client --</option>
                        {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                    </select>
                </div>

                {/* Description */}
                <div className={styles.inputGroup}>
                    <label htmlFor="description">Campaign Description</label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows="3"
                        required
                        disabled={updating}
                        placeholder="Briefly describe the campaign goals or theme..."
                    />
                </div>

                {/* Dates */}
                <div className={styles.gridTwoCols}> {/* Use grid for side-by-side dates */}
                    <div className={styles.inputGroup}>
                        <label htmlFor="startDate">Start Date</label>
                        <input
                            type="date"
                            id="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            required
                            disabled={updating}
                            className={styles.dateInput} // Added class if specific date styling needed
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="endDate">End Date</label>
                        <input
                            type="date"
                            id="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            required
                            disabled={updating}
                            min={startDate || ''} // Prevent end date before start date
                            className={styles.dateInput}
                        />
                    </div>
                </div>

                {/* Services */}
                <fieldset className={styles.fieldset}>
                    <legend className={styles.legend}>Service Counts</legend>
                    <div className={styles.gridThreeCols}> {/* Use grid for services */}
                        {serviceTypes.map(service => (
                            service.key !== 'prints' && (
                                <div key={service.key} className={styles.inputGroupCompact}>
                                    <label htmlFor={service.key}>{service.label}</label>
                                    <input
                                        type="number"
                                        id={service.key}
                                        min="0"
                                        step="1"
                                        value={services[service.key] || 0}
                                        onChange={(e) => handleServiceChange(service.key, e.target.value)}
                                        disabled={updating}
                                        className={styles.numberInput} // Added class for number styling
                                    />
                                </div>
                            )
                        ))}
                         {/* Special handling for Prints Count */}
                         <div className={styles.inputGroupCompact}>
                             <label htmlFor="prints">Prints (Jobs)</label>
                             <input
                                 type="number"
                                 id="prints"
                                 min="0"
                                 step="1"
                                 value={services['prints'] || 0}
                                 onChange={(e) => handleServiceChange('prints', e.target.value)}
                                 disabled={updating}
                                 className={styles.numberInput}
                             />
                         </div>
                    </div>
                     {/* Special handling for Prints Details */}
                     <div className={`${styles.inputGroup} ${styles.printsDetailsGroup}`}>
                         <label htmlFor="printsDetails">Prints Details (Optional)</label>
                         <textarea
                             id="printsDetails"
                             value={printsDetails}
                             onChange={(e) => setPrintsDetails(e.target.value)}
                             rows="2"
                             disabled={updating}
                             placeholder="Specify print job details (sizes, quantities, etc.)..."
                         />
                     </div>
                </fieldset>

                {/* Buttons */}
                <div className={styles.buttonGroup}>
                    <button
                        type="button"
                        onClick={() => navigate(`/campaigns/${campaignId}/plan`)} // Go back to plan page
                        className={`${styles.formButton} ${styles.cancelButton}`}
                        disabled={updating}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={`${styles.formButton} ${styles.submitButton}`}
                        disabled={updating}
                    >
                        {updating ? <span className={styles.loadingSpinner}></span> : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditCampaignPage;
